import * as APIModels from "../../../../models";
import { useState, useEffect, useRef, useCallback, FormEvent } from "react";
import { DataService } from "../../../../services/DataService";
import Select, { ActionMeta, components } from "react-select";
import { ClientService } from "../../../../services/ClientService";
import { apiPageLayoutToViewerModes } from "pdfjs-dist/types/web/ui_utils";
import PubSub, { countSubscriptions } from "pubsub-js";
import { string32 } from "pdfjs-dist/types/src/shared/util";
import { parseFullName } from "parse-full-name";
import SmartyStreets from "smartystreets-javascript-sdk";
import { Address } from "../../../../models/swagger/generated-submission-service";

type MyOptionType = { label: string; value: string };
type PartyAddressOptionType = {
    label: string;
    value: APIModels.ServiceContact;
};
type AddressOptionType = { label: string; value: APIModels.IAddress };
type PartyOptionType = {
    label: string;
    value: APIModels.Party;
};

export default function Party({
    attorneyId,
    party,
    partyTypes,
    onSaveParty,
    parties,
    currentDocument,
}: {
    attorneyId: string;
    party: APIModels.Party;
    partyTypes: APIModels.PartyType[];
    onSaveParty: (party: APIModels.Party) => void;
    parties: APIModels.Party[];
    currentDocument: APIModels.Document | null;
}) {
    const [completedParty, setCompletedParty] = useState(party);
    const [addresses, setAddresses] = useState([] as PartyAddressOptionType[]);
    const [defaultAddresses, setDefaultAddresses] = useState(
        [] as PartyAddressOptionType[]
    );
    const [partyTypeOptions, setPartyTypeOptions] = useState(
        [] as MyOptionType[]
    );
    const [stateOptions, setStateOptions] = useState([] as MyOptionType[]);
    const [errorMessage, setErrorMessage] = useState("");
    const [orgString, setOrgString] = useState("");

    const partyTypeRef = useRef(null);
    const fNameInput = useRef<HTMLInputElement>(null);
    const lNameInput = useRef<HTMLInputElement>(null);
    const orgInput = useRef<HTMLInputElement>(null);

    const streetInput = useRef<HTMLInputElement>(null);
    const cityInput = useRef<HTMLInputElement>(null);
    const zipInput = useRef<HTMLInputElement>(null);

    const eventAddress = new Address();

    const [eventAddressState, setEventAddressState] = useState(eventAddress);

    const [isEventAddressUpdated, setIsEventAddressUpdated] = useState(true);

    const [isAddressUpdated, setIsAddressUpdated] = useState(true);

    const SmartyCore = SmartyStreets.core;

    const _authId = "ed2ecd5d-9bec-a64e-adf1-fe22eef583a0";
    const _authToken = "1bONffCEsJ7zL97bnWLc";

    const clientBuilder = new SmartyCore.ClientBuilder(
        new SmartyCore.StaticCredentials(_authId, _authToken)
    );
    const client = clientBuilder.buildUsStreetApiClient();

    const [selectedDocument, setSelectedDocument] = useState(currentDocument);

    useEffect(() => {
        setSelectedDocument(currentDocument);
    }, [currentDocument]);

    useEffect(() => {
        setPartyTypeOptions(
            partyTypes.map((p, i) => {
                return { label: p.description ?? "", value: p.ePortalID ?? "" };
            })
        );

        DataService.getStates().then(
            (resp) => {
                const options = resp.map((v, i) => {
                    return { label: v.name ?? "", value: v.abbreviation ?? "" };
                });

                setStateOptions(options);
            },
            (error) => {
                console.log(error);
            }
        );

        PubSub.unsubscribe("plaintiffOrgEvent");
        PubSub.unsubscribe("defendantOrgEvent");
        PubSub.unsubscribe("plaintiffEvent");
        PubSub.unsubscribe("defendantEvent");
        PubSub.unsubscribe("addressEvent");

        PubSub.subscribe("addressEvent", addressHandler);
        PubSub.subscribe("plaintiffOrgEvent", partyOrganizationHandler);
        PubSub.subscribe("defendantOrgEvent", partyOrganizationHandler);
        PubSub.subscribe("plaintiffEvent", partyIndividualHandler);
        PubSub.subscribe("defendantEvent", partyIndividualHandler);
    }, [partyTypes]);

    const addressHandler = async (msg: any, data: string) => {
        const lookup = new SmartyStreets.usStreet.Lookup(data);

        const response = await client.send(lookup);

        const newAdress = new Address();

        newAdress.streetAddress = response.lookups[0].result[0].deliveryLine1;

        newAdress.unit =
            response.lookups[0].result[0].components.secondaryDesignator +
            " " +
            response.lookups[0].result[0].components.secondaryNumber;

        newAdress.city = response.lookups[0].result[0].components.cityName;

        newAdress.zip = response.lookups[0].result[0].components.zipCode;

        newAdress.state = response.lookups[0].result[0].components.state;

        setEventAddressState(newAdress);

        if (isEventAddressUpdated === true) setIsEventAddressUpdated(false);
        else if (isEventAddressUpdated === false)
            setIsEventAddressUpdated(true);
    };

    useEffect(() => {
        if (isEventAddressUpdated === false) {
            const temp = {
                ...completedParty,
                address: {
                    streetAddress: eventAddressState.streetAddress,
                    unit: eventAddressState.unit,
                    city: eventAddressState.city,
                    zip: eventAddressState.zip,
                    state: eventAddressState.state,
                },
            };
            setCompletedParty({
                ...temp,
            });
        }
        setIsEventAddressUpdated(true);
    }, [isEventAddressUpdated]);

    const populateOrgParty = (msg: any, data: string) => {
        const temp = { ...completedParty };

        if (data!.length > 0) {
            fNameInput.current?.removeAttribute("required");
            lNameInput.current?.removeAttribute("required");

            orgInput.current?.setAttribute("required", "required");
        } else {
            fNameInput.current?.setAttribute("required", "required");
            lNameInput.current?.setAttribute("required", "required");
            orgInput.current?.setAttribute("required", "required");
        }

        let partyName = "";
        if (msg === "plaintiffOrgEvent") {
            partyName = "Plaintiff";

            const party = partyTypes.find(
                (c) => c.description?.toLowerCase() === partyName.toLowerCase()
            );

            const temp = {
                ...completedParty,
                name: data,
                partyTypeCode: party?.ePortalID,
                partyType: party?.description,
            };

            setCompletedParty({ ...temp });
        } else if (msg === "defendantOrgEvent") {
            partyName = "Defendant";

            const party = partyTypes.find(
                (c) => c.description?.toLowerCase() === partyName.toLowerCase()
            );

            setCompletedParty({
                ...temp,
                name: data,
                partyTypeCode: party?.ePortalID,
                partyType: party?.description,
            });
        }
    };

    const populateIndividualParty = (msg: any, data: string) => {
        const name = parseFullName(data);

        let partyName = "";

        if (name.first!.length > 0 && name.last!.length > 0) {
            orgInput.current?.removeAttribute("required");
        } else {
            fNameInput.current?.setAttribute("required", "required");
            lNameInput.current?.setAttribute("required", "required");
            orgInput.current?.setAttribute("required", "required");
        }
        if (msg === "plaintiffEvent") {
            partyName = "Plaintiff";

            const party = partyTypes.find(
                (c) => c.description?.toLowerCase() === partyName.toLowerCase()
            );

            const temp = {
                ...completedParty,
                firstName: name.first,
                middleName: name.middle,
                lastName: name.last,
                partyTypeCode: party?.ePortalID,
                partyType: party?.description,
            };

            setCompletedParty({ ...temp });
        } else if (msg === "defendantEvent") {
            partyName = "Defendant";

            const party = partyTypes.find(
                (c) => c.description?.toLowerCase() === partyName.toLowerCase()
            );

            const temp = {
                ...completedParty,
                firstName: name.first,
                middleName: name.middle,
                lastName: name.last,
                partyTypeCode: party?.ePortalID,
                partyType: party?.description,
            };

            setCompletedParty({ ...temp });
        }
    };
    const partyOrganizationHandler = (msg: any, data: string) => {
        populateOrgParty(msg, data);

        if (isAddressUpdated === true) setIsAddressUpdated(false);
        else if (isAddressUpdated === false) setIsAddressUpdated(true);
    };

    const partyIndividualHandler = (msg: any, data: string) => {
        populateIndividualParty(msg, data);

        if (isAddressUpdated === true) setIsAddressUpdated(false);
        else if (isAddressUpdated === false) setIsAddressUpdated(true);
    };

    useEffect(() => {
        if (isAddressUpdated === false) {
            if (
                completedParty.partyType != undefined &&
                (completedParty.partyType!.toLowerCase().indexOf("plaintiff") >
                    -1 ||
                    completedParty.partyType!.toLowerCase().indexOf("pet") >
                        -1 ||
                    completedParty.partyType!.toLowerCase().indexOf("assign") >
                        -1)
            ) {
                const addressOption = addresses.filter(
                    (a) => a.label.indexOf("Attorney") > -1
                )[0];
                console.log(addressOption);

                const temp = {
                    ...completedParty,
                    address: {
                        streetAddress:
                            addressOption?.value.address?.streetAddress,
                        unit: addressOption?.value.address?.unit,
                        city: addressOption?.value.address?.city,
                        zip: addressOption?.value.address?.zip,
                        state: addressOption?.value.address?.state,
                    },
                };
                setCompletedParty({ ...temp });
            } else {
                const temp = {
                    ...completedParty,
                    address: {
                        streetAddress: "",
                        unit: "",
                        city: "",
                        zip: "",
                        state: "",
                    },
                };
                setCompletedParty({ ...temp });
            }
        }

        setIsAddressUpdated(true);
    }, [isAddressUpdated]);

    useEffect(() => {
        ClientService.getAddresses({ attorneyId }).then(
            (resp) => {
                if (resp.successful && resp.data) {
                    const options = resp.data.map((v, i) => {
                        return { label: v.fullName ?? "", value: v };
                    });
                    setDefaultAddresses(options);
                }
            },
            (error) => {
                console.log(error);
            }
        );
    }, []);

    useEffect(() => {
        setCompletedParty(party);
    }, [party]);

    useEffect(() => {
        const options = parties.map((v, i) => {
            return {
                label: v.name ?? v.firstName + " " + v.lastName,
                value: {
                    fullName: v.name ?? v.firstName + " " + v.lastName,
                    address: v.address,
                },
            };
        });

        const addressOptions = [...defaultAddresses, ...options];
        setAddresses(addressOptions);
    }, [parties]);

    const onPartyTypeChange = (
        value: MyOptionType | null,
        actionMeta: ActionMeta<MyOptionType>
    ) => {
        const partyTypeName = value?.label ?? "";
        setErrorMessage("");

        if (
            partyTypeName.toLowerCase().indexOf("plaintiff") > -1 ||
            partyTypeName.toLowerCase().indexOf("pet") > -1 ||
            partyTypeName.toLowerCase().indexOf("assign") > -1
        ) {
            const addressOption = addresses.filter(
                (a) => a.label.indexOf("Attorney") > -1
            )[0];

            setCompletedParty({
                ...completedParty,
                partyType: value?.label,
                partyTypeCode: value?.value,
                address: {
                    streetAddress: addressOption?.value.address?.streetAddress,
                    unit: addressOption?.value.address?.unit,
                    city: addressOption?.value.address?.city,
                    zip: addressOption?.value.address?.zip,
                    state: addressOption?.value.address?.state,
                },
            });
        } else {
            setCompletedParty({
                ...completedParty,
                partyType: value?.label,
                partyTypeCode: value?.value,
            });
        }
    };

    const onClickInsurance = () => {
        const available = addresses.filter((a) => a.label.indexOf("DFS") > -1);
        if (available && available.length == 1) {
            setCompletedParty({
                ...completedParty,
                address: {
                    streetAddress: available[0].value.address?.streetAddress,
                    unit: available[0].value.address?.unit,
                    city: available[0].value.address?.city,
                    zip: available[0].value.address?.zip,
                    state: available[0].value.address?.state,
                },
            });
        }
    };

    const onPresetAddressChange = (
        value: PartyAddressOptionType | null,
        actionMeta: ActionMeta<PartyAddressOptionType>
    ) => {
        setCompletedParty({
            ...completedParty,
            address: {
                streetAddress: value?.value.address?.streetAddress,
                unit: value?.value.address?.unit,
                city: value?.value.address?.city,
                zip: value?.value.address?.zip,
                state: value?.value.address?.state,
            },
        });
    };

    const onDocumentAddressChange = (
        value: AddressOptionType | null,
        actionMeta: ActionMeta<AddressOptionType>
    ) => {
        setCompletedParty({
            ...completedParty,
            address: {
                streetAddress: value?.value.streetAddress,
                unit: value?.value.unit,
                city: value?.value.city,
                zip: value?.value.zip,
                state: value?.value.state,
            },
        });
    };

    const onDetectedPartyChange = (
        value: PartyOptionType | null,
        actionMeta: ActionMeta<PartyOptionType>
    ) => {
        setCompletedParty({
            ...completedParty,
            name: value?.value.firstName ? undefined : value?.value.name,
            firstName: value?.value.firstName,
            middleName: value?.value.middleName,
            lastName: value?.value.lastName,
        });

        if (value?.value.firstName) {
            fNameInput.current?.setAttribute("required", "required");
            lNameInput.current?.setAttribute("required", "required");
            orgInput.current?.removeAttribute("required");
        } else {
            fNameInput.current?.removeAttribute("required");
            lNameInput.current?.removeAttribute("required");

            orgInput.current?.setAttribute("required", "required");

            if (
                value?.value.name &&
                (value?.value.name.toLowerCase().indexOf("geico") > -1 ||
                    value?.value.name.toLowerCase().indexOf("usaa") > -1 ||
                    value?.value.name.toLowerCase().indexOf("insura") > -1 ||
                    value?.value.name.toLowerCase().indexOf("indemnity") > -1 ||
                    value?.value.name.toLowerCase().indexOf("state farm") > -1)
            ) {
                onClickInsurance();
            }
        }
    };

    const onOrganizationNameChange = (
        event: React.FormEvent<HTMLInputElement>
    ) => {
        event.preventDefault();

        setCompletedParty({
            ...completedParty,
            name: event.currentTarget.value,
        });

        if (event.currentTarget.value.length > 0) {
            fNameInput.current?.removeAttribute("required");
            lNameInput.current?.removeAttribute("required");

            orgInput.current?.setAttribute("required", "required");
        } else {
            fNameInput.current?.setAttribute("required", "required");
            lNameInput.current?.setAttribute("required", "required");
            orgInput.current?.setAttribute("required", "required");
        }
    };

    const onOrganizationNameBlur = (
        event: React.FormEvent<HTMLInputElement>
    ) => {
        if (
            event.currentTarget.value.toLowerCase().indexOf("geico") > -1 ||
            event.currentTarget.value.toLowerCase().indexOf("usaa") > -1 ||
            event.currentTarget.value.toLowerCase().indexOf("insura") > -1 ||
            event.currentTarget.value.toLowerCase().indexOf("indemnity") > -1 ||
            event.currentTarget.value.toLowerCase().indexOf("state farm") > -1
        ) {
            onClickInsurance();
        }
    };

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.stopPropagation();
        event.preventDefault();
        event.nativeEvent.stopImmediatePropagation();

        if (completedParty.partyTypeCode && completedParty.partyTypeCode != "")
            onSaveParty(completedParty);
        else setErrorMessage("Please select the party type");

        return false;
    };

    const filterOption = (option: MyOptionType, inputValue: string): boolean =>
        (
            option.label
                .toString()
                .toLowerCase()
                .match(inputValue.toLowerCase()) || []
        ).length > 0;

    return (
        <form
            onSubmit={(e) => {
                e.stopPropagation();
                e.preventDefault();
                e.nativeEvent.stopImmediatePropagation();
                return onSubmit(e);
            }}
        >
            <div className="row mb-3">
                <div className="col-sm-6">
                    <label htmlFor="selPartyType" className="form-label">
                        Party Type
                    </label>
                    <Select
                        ref={partyTypeRef}
                        id="selPartyType"
                        name="selPartyType"
                        value={partyTypeOptions.filter(
                            (c) => c.value === completedParty.partyTypeCode
                        )}
                        filterOption={filterOption}
                        options={partyTypeOptions}
                        onChange={onPartyTypeChange}
                        required
                    />
                </div>
                <div className="col-sm-6">
                    <label htmlFor="selDetectedParties" className="form-label">
                        Detected Parties
                    </label>
                    <Select
                        id="selDetectedParties"
                        name="selDetectedParties"
                        options={
                            selectedDocument?.detectedParties?.map((v, i) => {
                                return {
                                    label:
                                        v.name ??
                                        v.firstName + " " + v.lastName,
                                    value: v,
                                };
                            }) || []
                        }
                        onChange={onDetectedPartyChange}
                    />
                </div>
            </div>
            <div className="row mb-3">
                <div className="col-sm-4">
                    <label htmlFor="txtFName" className="form-label">
                        First Name
                    </label>
                    <div className="input-group">
                        <input
                            ref={fNameInput}
                            type="text"
                            className="form-control"
                            id="txtFName"
                            placeholder="First Name"
                            aria-describedby="inputGroupPrepend"
                            value={completedParty.firstName ?? ""}
                            onChange={(event) => {
                                setCompletedParty({
                                    ...completedParty,
                                    firstName: event.currentTarget.value,
                                });

                                if (event.currentTarget.value.length > 0) {
                                    orgInput.current?.removeAttribute(
                                        "required"
                                    );

                                    lNameInput.current?.setAttribute(
                                        "required",
                                        "required"
                                    );
                                } else {
                                    orgInput.current?.setAttribute(
                                        "required",
                                        "required"
                                    );
                                }
                            }}
                            required
                        />
                    </div>
                </div>
                <div className="col-sm-4">
                    <label htmlFor="txtMName" className="form-label">
                        Middle Name
                    </label>
                    <div className="input-group">
                        <input
                            type="text"
                            className="form-control"
                            id="txtMName"
                            placeholder="Middle Name"
                            aria-describedby="inputGroupPrepend"
                            value={completedParty.middleName ?? ""}
                            onChange={(event) => {
                                setCompletedParty({
                                    ...completedParty,
                                    middleName: event.currentTarget.value,
                                });
                            }}
                        />
                    </div>
                </div>
                <div className="col-sm-4">
                    <label htmlFor="txtLName" className="form-label">
                        Last Name
                    </label>
                    <div className="input-group">
                        <input
                            ref={lNameInput}
                            type="text"
                            className="form-control"
                            id="txtLName"
                            placeholder="Last Name"
                            aria-describedby="inputGroupPrepend"
                            value={completedParty.lastName ?? ""}
                            onChange={(event) => {
                                console.log("Hit Last name change");
                                setCompletedParty({
                                    ...completedParty,
                                    lastName: event.currentTarget.value,
                                });

                                if (event.currentTarget.value.length > 0) {
                                    orgInput.current?.removeAttribute(
                                        "required"
                                    );

                                    fNameInput.current?.setAttribute(
                                        "required",
                                        "required"
                                    );
                                } else {
                                    orgInput.current?.setAttribute(
                                        "required",
                                        "required"
                                    );
                                }
                            }}
                            required
                        />
                    </div>
                </div>
            </div>
            <div className="row mb-3">
                <div className="col-sm-12">
                    <label htmlFor="txtOrgName" className="form-label">
                        Organization Name
                    </label>
                    <div className="input-group">
                        <input
                            ref={orgInput}
                            type="text"
                            className="form-control"
                            id="txtOrgName"
                            placeholder="Organization Name"
                            aria-describedby="inputGroupPrepend"
                            value={completedParty.name ?? ""}
                            onChange={onOrganizationNameChange}
                            onBlur={onOrganizationNameBlur}
                            required
                        />
                    </div>
                </div>
            </div>
            <div className="row mb-3">
                <div className="col-sm-12">
                    <div className="row mb-3">
                        <div className="col-sm-4">
                            <label
                                htmlFor="btnInsertDFSAddress"
                                className="form-label"
                            >
                                Copy From
                            </label>
                            <button
                                id="btnInsertDFSAddress"
                                className="btn btn-primary"
                                onClick={onClickInsurance}
                                type="button"
                            >
                                Insurance Company
                            </button>
                        </div>
                        <div className="col-sm-4">
                            <label
                                htmlFor="selOtherPartyAddresses"
                                className="form-label"
                            >
                                Other Parties
                            </label>
                            <Select
                                id="selOtherPartyAddresses"
                                name="selOtherPartyAddresses"
                                options={addresses}
                                onChange={onPresetAddressChange}
                            />
                        </div>
                        <div className="col-sm-4">
                            <label
                                htmlFor="selDocAddresses"
                                className="form-label"
                            >
                                Detected
                            </label>
                            <Select
                                id="selDocAddresses"
                                name="selDocAddresses"
                                options={
                                    selectedDocument?.detectedAddresses?.map(
                                        (v, i) => {
                                            return {
                                                label: v.fullAddress ?? "",
                                                value: v,
                                            };
                                        }
                                    ) || []
                                }
                                onChange={onDocumentAddressChange}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row mb-3">
                <div className="col-sm-6">
                    <label htmlFor="txtStreetAddr" className="form-label">
                        Street Address
                    </label>
                    <div className="input-group">
                        <input
                            ref={streetInput}
                            type="text"
                            className="form-control"
                            id="txtStreetAddr"
                            placeholder="Street Address"
                            aria-describedby="inputGroupPrepend"
                            value={completedParty.address?.streetAddress ?? ""}
                            onChange={(event) =>
                                setCompletedParty({
                                    ...completedParty,
                                    address: {
                                        ...completedParty.address,
                                        streetAddress:
                                            event.currentTarget.value,
                                    },
                                })
                            }
                            required
                        />
                    </div>
                </div>
                <div className="col-sm-6">
                    <label htmlFor="txtStreetAddress2" className="form-label">
                        Street Address 2
                    </label>
                    <div className="input-group">
                        <input
                            type="text"
                            className="form-control"
                            id="txtStreetAddress2"
                            placeholder="Street Address 2"
                            aria-describedby="inputGroupPrepend"
                            value={completedParty.address?.unit ?? ""}
                            onChange={(event) =>
                                setCompletedParty({
                                    ...completedParty,
                                    address: {
                                        ...completedParty.address,
                                        unit: event.currentTarget.value ?? "",
                                    },
                                })
                            }
                        />
                    </div>
                </div>
            </div>
            <div className="row mb-3">
                <div className="col-sm-4">
                    <label htmlFor="txtCity" className="form-label">
                        City
                    </label>
                    <div className="input-group">
                        <input
                            ref={cityInput}
                            type="text"
                            className="form-control"
                            id="txtCity"
                            placeholder="City"
                            aria-describedby="inputGroupPrepend"
                            value={completedParty.address?.city ?? ""}
                            onChange={(event) =>
                                setCompletedParty({
                                    ...completedParty,
                                    address: {
                                        ...completedParty.address,
                                        city: event.currentTarget.value,
                                    },
                                })
                            }
                            required
                        />
                    </div>
                </div>
                <div className="col-sm-4">
                    <label htmlFor="txtMName" className="form-label">
                        State
                    </label>
                    <Select
                        id="selState"
                        name="selState"
                        value={stateOptions.filter(
                            (c) => c.value === completedParty.address?.state
                        )}
                        options={stateOptions}
                        onChange={(
                            value: MyOptionType | null,
                            actionMeta: ActionMeta<MyOptionType>
                        ) => {
                            setCompletedParty({
                                ...completedParty,
                                address: {
                                    ...completedParty.address,
                                    state: value?.value,
                                },
                            });
                        }}
                        required
                    />
                </div>
                <div className="col-sm-4">
                    <label htmlFor="txtZip" className="form-label">
                        Zip
                    </label>
                    <div className="input-group">
                        <input
                            ref={zipInput}
                            type="text"
                            className="form-control"
                            id="txtZip"
                            placeholder="Zip"
                            aria-describedby="inputGroupPrepend"
                            value={completedParty.address?.zip ?? ""}
                            onChange={(event) =>
                                setCompletedParty({
                                    ...completedParty,
                                    address: {
                                        ...completedParty.address,
                                        zip: event.currentTarget.value,
                                    },
                                })
                            }
                            required
                        />
                    </div>
                </div>
            </div>
            <div className="row mb-3">
                <div style={{ color: "red", textAlign: "center" }}>
                    {errorMessage}
                </div>
                <button className="btn btn-primary" type="submit">
                    Save Party
                </button>
            </div>
        </form>
    );
}
